/**
 * Approved performance increments waiting to hit payroll.
 *
 * A published review with an approved increment % creates a pending
 * "salary revision". It auto-applies on its effective date (nightly job) —
 * until then HR can edit the % / date or cancel it here.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { TrendingUp, Loader2, Check, X, Save } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { formatErr, inputCls, fmtINR } from '../../../components/admin/billing/shared';

const STATUS_STYLE = {
  pending: 'bg-amber-100 text-amber-800',
  applied: 'bg-emerald-100 text-emerald-800',
  cancelled: 'bg-slate-100 text-slate-500',
};

export const SalaryRevisionsCard = ({ onApplied }) => {
  const { authHeader } = useAuth();
  const [rows, setRows] = useState([]);
  const [today, setToday] = useState('');
  const [busy, setBusy] = useState('');
  const [edits, setEdits] = useState({});
  const [showHistory, setShowHistory] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/performance/salary-revisions`, { headers: authHeader() });
      setRows(data.revisions || []);      setToday(data.today || '');
    } catch { /* HR-only card — silently hide for others */ }
    finally { setLoaded(true); }
  }, [authHeader]);

  useEffect(() => { load(); }, [load]);

  const act = async (rev, path) => {
    setBusy(rev.id);
    try {
      await axios.post(`${API}/performance/salary-revisions/${rev.id}/${path}`, {}, { headers: authHeader() });
      toast.success(path === 'apply' ? 'Salary updated — payroll will use the new base' : 'Revision cancelled');
      await load();
      if (path === 'apply' && onApplied) onApplied();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(''); }
  };

  const save = async (rev) => {
    const e = edits[rev.id] || {};
    setBusy(rev.id);
    try {
      await axios.patch(`${API}/performance/salary-revisions/${rev.id}`, {
        increment_pct: e.increment_pct === undefined ? undefined : Number(e.increment_pct),
        effective_from: e.effective_from,
      }, { headers: authHeader() });
      toast.success('Revision updated');
      setEdits((s) => ({ ...s, [rev.id]: undefined }));
      await load();
    } catch (err) { toast.error(formatErr(err)); }
    finally { setBusy(''); }
  };

  if (!loaded || !rows.length) return null;

  const pending = rows.filter((r) => r.status === 'pending');
  const history = rows.filter((r) => r.status !== 'pending');
  const visible = showHistory ? [...pending, ...history] : pending;
  if (!visible.length && !history.length) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="salary-revisions-card">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
        <TrendingUp size={15} className="text-teal-600" />
        <h2 className="text-sm font-semibold text-slate-800">Approved performance increments</h2>
        <span className="text-xs text-slate-400">auto-applies on the effective date</span>
        {!!history.length && (
          <button
            onClick={() => setShowHistory((v) => !v)}
            className="ml-auto text-xs text-slate-500 hover:text-slate-800"
            data-testid="toggle-revision-history"
          >
            {showHistory ? 'Hide history' : `Show history (${history.length})`}
          </button>
        )}
      </div>
      <div className="divide-y divide-slate-100">
        {!visible.length && (
          <div className="p-3 text-xs text-slate-500">No increments waiting to be applied.</div>
        )}
        {visible.map((r) => {
          const e = edits[r.id] || {};
          const dirty = e.increment_pct !== undefined || e.effective_from !== undefined;
          return (
            <div key={r.id} className="p-3 flex flex-wrap items-center gap-3" data-testid={`revision-${r.id}`}>
              <div className="min-w-[160px]">
                <div className="text-sm font-medium text-slate-900">{r.staff_name}</div>
                <div className="text-xs text-slate-500">{r.cycle_name} · band {r.band || '—'}</div>
              </div>
              <div className="text-xs text-slate-600 font-mono">
                ₹ {fmtINR(r.old_base_salary)} → <span className="text-emerald-700 font-semibold">₹ {fmtINR(r.new_base_salary)}</span>
              </div>
              {r.status === 'pending' ? (
                <>
                  <div className="flex items-center gap-1">
                    <input
                      type="number" step="0.1"
                      className={`${inputCls} !w-20`}
                      value={e.increment_pct ?? r.increment_pct}
                      onChange={(ev) => setEdits((s) => ({ ...s, [r.id]: { ...e, increment_pct: ev.target.value } }))}
                      data-testid={`revision-pct-${r.id}`}
                    />
                    <span className="text-xs text-slate-500">%</span>
                  </div>
                  <input
                    type="date" className={`${inputCls} !w-40`}
                    value={e.effective_from ?? r.effective_from}
                    onChange={(ev) => setEdits((s) => ({ ...s, [r.id]: { ...e, effective_from: ev.target.value } }))}
                    data-testid={`revision-date-${r.id}`}
                  />
                  <div className="flex items-center gap-1.5 ml-auto">
                    {dirty && (
                      <button
                        onClick={() => save(r)} disabled={busy === r.id}
                        className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
                        data-testid={`revision-save-${r.id}`}
                      >
                        {busy === r.id ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
                      </button>
                    )}
                    <button
                      onClick={() => act(r, 'apply')} disabled={busy === r.id}
                      className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50"
                      data-testid={`revision-apply-${r.id}`}
                    >
                      <Check size={12} /> Apply now
                    </button>
                    <button
                      onClick={() => act(r, 'cancel')} disabled={busy === r.id}
                      className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50"
                      data-testid={`revision-cancel-${r.id}`}
                    >
                      <X size={12} /> Cancel
                    </button>
                  </div>
                </>
              ) : (
                <div className="flex items-center gap-2 ml-auto text-xs">
                  <span className="text-slate-500">{r.increment_pct}% · effective {r.effective_from}</span>
                  <span className={`px-2 py-0.5 rounded-full font-medium ${STATUS_STYLE[r.status]}`}>{r.status}</span>
                </div>
              )}
              {r.status === 'pending' && r.effective_from <= today && (
                <span className="text-[11px] text-amber-700 w-full">Due — will be applied by tonight's job.</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SalaryRevisionsCard;
